import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api";
import { Card } from "../ui/CCard";
import { Tooltip } from "../ui/Tooltip";
import { DollarSign, Wallet, Clock, Receipt, Loader2, Info, AlertTriangle, Coins, ChevronRight, PiggyBank } from "lucide-react";
import { usePeriodFilter, PeriodPicker, formatPeriodLabel } from "./financePeriod";

const InfoTip = ({ text, position = "top" }) => (
  <Tooltip content={<span className="block max-w-[220px]">{text}</span>} position={position}>
    <Info className="w-3.5 h-3.5 text-secondary-400 hover:text-secondary-600 cursor-help" />
  </Tooltip>
);

const METHOD_LABELS = {
  card: "Card",
  upi: "UPI",
  netbanking: "Netbanking",
  wallet: "Wallet",
  emi: "EMI",
  "Cash / At Clinic": "Cash / At Clinic",
  Unknown: "Unknown",
};

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

// `filters` renders in the same row as the period picker (see PeriodPicker's `leading`).
export default function FinanceSummary({ doctorId = null, branchId = null, showDoctorColumn = false, filters = null }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { period, setPeriod, customStart, setCustomStart, customEnd, setCustomEnd, range } = usePeriodFilter();

  const goToDetails = (type) => {
    const params = new URLSearchParams({ type, period });
    if (doctorId) params.set("doctorId", doctorId);
    if (branchId) params.set("branchId", branchId);
    if (period === "custom" && range) {
      params.set("start", range.start);
      params.set("end", range.end);
    }
    navigate(`/finance/payout-details?${params.toString()}`);
  };

  useEffect(() => {
    if (!range) return; // custom range picked but not fully filled in yet
    if (range.end < range.start) return; // invalid range, wait for the user to fix it
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .getFinanceSummary({
        doctor_id: doctorId || undefined,
        branch_id: branchId || undefined,
        start_date: range.start,
        end_date: range.end,
      })
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch((err) => {
        console.error("FinanceSummary: failed to load", err);
        if (!cancelled) setError("Unable to load finance data right now.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [doctorId, branchId, period, customStart, customEnd]);

  const periodPicker = (
    <PeriodPicker
      period={period}
      onPeriodChange={setPeriod}
      customStart={customStart}
      onCustomStartChange={setCustomStart}
      customEnd={customEnd}
      onCustomEndChange={setCustomEnd}
      resolvedLabel={data?.period ? `${formatPeriodLabel(data.period.start_date)} – ${formatPeriodLabel(data.period.end_date)}` : null}
      leading={filters}
    />
  );

  if (period === "custom" && !range) {
    return (
      <div className="space-y-6 animate-fade-in-up">
        {periodPicker}
        <Card className="p-12 text-center text-secondary-400">Pick a start and end date to see finance data.</Card>
      </div>
    );
  }

  if (range && range.end < range.start) {
    return (
      <div className="space-y-6 animate-fade-in-up">
        {periodPicker}
        <Card className="p-12 text-center text-secondary-400">End date can't be before start date.</Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6 animate-fade-in-up">
        {periodPicker}
        <Card className="p-12 flex flex-col items-center justify-center text-secondary-400">
          <Loader2 className="w-6 h-6 animate-spin mb-2" />
          Loading finance data...
        </Card>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="space-y-6 animate-fade-in-up">
        {periodPicker}
        <Card className="p-12 text-center text-secondary-400">{error || "No finance data available."}</Card>
      </div>
    );
  }

  const { summary, payout_breakdown, revenue_by_payment_mode, payment_methods, recent_transactions } = data;
  const onlineTotal = revenue_by_payment_mode.online + revenue_by_payment_mode.at_clinic;
  const onlinePct = onlineTotal > 0 ? (revenue_by_payment_mode.online / onlineTotal) * 100 : 0;

  return (
    <div className="space-y-6 animate-fade-in-up">
      {periodPicker}

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        <Card className="p-5 flex flex-col justify-between h-32 hover:scale-[1.02] active:scale-[0.98] transition-all cursor-default group">
          <div className="flex justify-between items-start">
            <span className="text-secondary-500 font-medium text-sm uppercase tracking-wide flex items-center gap-1.5">
              Total Earnings
              <InfoTip text="Confirmed revenue for this period only: at-clinic fees, plus online payments for a consultation that actually happened — a paid booking with no consultation recorded doesn't count here (see Missed Revenue once its date passes, or Advance Collected while it's still upcoming). Net Payout above is this period's settled share; Pending Payout is shown separately since it spans every period, not just this one." />
            </span>
            <div className="p-2 bg-green-100 text-green-600 rounded-lg group-hover:rotate-12 transition-transform">
              <DollarSign size={20} />
            </div>
          </div>
          <span className="text-4xl font-heading font-bold text-secondary-900 block">{money(summary.total_earnings)}</span>
        </Card>

        <Card
          onClick={() => goToDetails("net_payout")}
          className="p-5 flex flex-col justify-between h-32 hover:scale-[1.02] active:scale-[0.98] transition-all cursor-pointer group"
        >
          <div className="flex justify-between items-start">
            <span className="text-secondary-500 font-medium text-sm uppercase tracking-wide flex items-center gap-1.5">
              Net Payout
              <InfoTip text="Money actually settled to you: at-clinic fees (received immediately) plus online payments that ops has completed a payout for. Click to see the transactions behind this." />
            </span>
            <div className="p-2 bg-blue-100 text-blue-600 rounded-lg group-hover:rotate-12 transition-transform">
              <Wallet size={20} />
            </div>
          </div>
          <div className="flex items-end justify-between">
            <span className="text-4xl font-heading font-bold text-secondary-900 block">{money(summary.net_payout)}</span>
            <ChevronRight size={16} className="text-secondary-300 group-hover:text-secondary-500 transition-colors mb-1" />
          </div>
        </Card>

        <Card
          onClick={() => goToDetails("pending_payout")}
          className="p-5 flex flex-col justify-between h-32 hover:scale-[1.02] active:scale-[0.98] transition-all cursor-pointer group"
        >
          <div className="flex justify-between items-start">
            <span className="text-secondary-500 font-medium text-sm uppercase tracking-wide flex items-center gap-1.5">
              Pending Payout
              <InfoTip text="Collected online from patients, consultation completed, but not yet paid out to you by our ops team — still on its way. Always shows everything currently owed across all time, regardless of the period filter above, since money still needs settling no matter which month the consultation happened in. Click to see which consultations these are." />
            </span>
            <div className="p-2 bg-amber-100 text-amber-600 rounded-lg group-hover:rotate-12 transition-transform">
              <Coins size={20} />
            </div>
          </div>
          <div className="flex items-end justify-between">
            <div>
              <span className="text-4xl font-heading font-bold text-secondary-900 block">{money(summary.pending_payout)}</span>
              <span className="text-xs text-secondary-400">
                All time{payout_breakdown.pending_payout_count > 0 ? ` · ${payout_breakdown.pending_payout_count} appointment${payout_breakdown.pending_payout_count === 1 ? "" : "s"}` : ""}
              </span>
            </div>
            <ChevronRight size={16} className="text-secondary-300 group-hover:text-secondary-500 transition-colors mb-1" />
          </div>
        </Card>

        <Card
          onClick={() => goToDetails("pending_collections")}
          className="p-5 flex flex-col justify-between h-32 hover:scale-[1.02] active:scale-[0.98] transition-all cursor-pointer group"
        >
          <div className="flex justify-between items-start">
            <span className="text-secondary-500 font-medium text-sm uppercase tracking-wide flex items-center gap-1.5">
              Pending Collections
              <InfoTip text="Fees for appointments in this period dated today or later that are still Unpaid — still realistically collectible. Doesn't include appointments whose date has already passed (see Missed Revenue). Click to see which appointments these are." />
            </span>
            <div className="p-2 bg-amber-100 text-amber-600 rounded-lg group-hover:rotate-12 transition-transform">
              <Clock size={20} />
            </div>
          </div>
          <div className="flex items-end justify-between">
            <div>
              <span className="text-4xl font-heading font-bold text-secondary-900 block">{money(summary.pending_collections)}</span>
              {payout_breakdown.unpaid_count > 0 && (
                <span className="text-xs text-secondary-400">{payout_breakdown.unpaid_count} appointment{payout_breakdown.unpaid_count === 1 ? "" : "s"}</span>
              )}
            </div>
            <ChevronRight size={16} className="text-secondary-300 group-hover:text-secondary-500 transition-colors mb-1" />
          </div>
        </Card>

        <Card
          onClick={() => goToDetails("advance_collected")}
          className="p-5 flex flex-col justify-between h-32 hover:scale-[1.02] active:scale-[0.98] transition-all cursor-pointer group"
        >
          <div className="flex justify-between items-start">
            <span className="text-secondary-500 font-medium text-sm uppercase tracking-wide flex items-center gap-1.5">
              Advance Collected
              <InfoTip text="Paid online for an upcoming appointment that hasn't happened yet — too early to count as earned (see Total Earnings) or missed. Shown as the fee charged to the patient, same as Pending Collections — nothing has been settled/paid out yet, so there's no gateway deduction to net against. Moves to Net/Pending Payout once the consultation happens, or Missed Revenue if the date passes with no consultation." />
            </span>
            <div className="p-2 bg-purple-100 text-purple-600 rounded-lg group-hover:rotate-12 transition-transform">
              <PiggyBank size={20} />
            </div>
          </div>
          <div className="flex items-end justify-between">
            <div>
              <span className="text-4xl font-heading font-bold text-secondary-900 block">{money(summary.advance_collected)}</span>
              {payout_breakdown.advance_count > 0 && (
                <span className="text-xs text-secondary-400">{payout_breakdown.advance_count} appointment{payout_breakdown.advance_count === 1 ? "" : "s"}</span>
              )}
            </div>
            <ChevronRight size={16} className="text-secondary-300 group-hover:text-secondary-500 transition-colors mb-1" />
          </div>
        </Card>

        <Card
          onClick={() => goToDetails("missed_revenue")}
          className="p-5 flex flex-col justify-between h-32 hover:scale-[1.02] active:scale-[0.98] transition-all cursor-pointer group"
        >
          <div className="flex justify-between items-start">
            <span className="text-secondary-500 font-medium text-sm uppercase tracking-wide flex items-center gap-1.5">
              Missed Revenue
              <InfoTip text="Appointments whose date has already passed and will never bring in revenue: never paid at all, or paid online with no consultation ever recorded — ops will never pay those out. Click to see the list." />
            </span>
            <div className="p-2 bg-red-100 text-red-600 rounded-lg group-hover:rotate-12 transition-transform">
              <AlertTriangle size={20} />
            </div>
          </div>
          <div className="flex items-end justify-between">
            <div>
              <span className="text-4xl font-heading font-bold text-secondary-900 block">{money(summary.missed_revenue)}</span>
              {payout_breakdown.missed_count > 0 && (
                <span className="text-xs text-secondary-400">{payout_breakdown.missed_count} appointment{payout_breakdown.missed_count === 1 ? "" : "s"}</span>
              )}
            </div>
            <ChevronRight size={16} className="text-secondary-300 group-hover:text-secondary-500 transition-colors mb-1" />
          </div>
        </Card>

        <Card className="p-5 flex flex-col justify-between h-32 hover:scale-[1.02] active:scale-[0.98] transition-all cursor-default group">
          <div className="flex justify-between items-start">
            <span className="text-secondary-500 font-medium text-sm uppercase tracking-wide flex items-center gap-1.5">
              Avg / Consult
              <InfoTip text="Total Earnings divided by the number of confirmed consultations in this period (not every paid booking — see Total Earnings)." />
            </span>
            <div className="p-2 bg-primary-100 text-primary-600 rounded-lg group-hover:rotate-12 transition-transform">
              <Receipt size={20} />
            </div>
          </div>
          <span className="text-4xl font-heading font-bold text-secondary-900 block">{money(summary.avg_per_consult)}</span>
        </Card>
      </div>

      {/* Payout breakdown */}
      <Card className="p-6">
        <h3 className="text-lg font-bold text-secondary-800 mb-4 flex items-center gap-1.5">
          Payout Breakdown
          <InfoTip text="Two separate figures on two different spans, not two halves of one total — they won't sum to Total Earnings above. Net Payout is what settled during this period; Pending Payout always spans all time, since it's everything currently owed to you regardless of which month the consultation happened in." />
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="p-4 rounded-xl bg-green-50 border border-green-100">
            <span className="text-[11px] uppercase tracking-wide font-semibold text-green-700">Net Payout · This Period</span>
            <div className="text-2xl font-bold text-green-800 mt-1">{money(payout_breakdown.net_payout)}</div>
          </div>
          <div className="p-4 rounded-xl bg-amber-50 border border-amber-100">
            <span className="text-[11px] uppercase tracking-wide font-semibold text-amber-700">Pending Payout · All Time</span>
            <div className="text-2xl font-bold text-amber-800 mt-1">{money(payout_breakdown.pending_payout)}</div>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mt-6 text-sm">
          <div>
            <span className="text-secondary-400 flex items-center gap-1 text-xs uppercase tracking-wide">
              Gateway Fee
              <InfoTip text="Total fee charged by the payment gateway (Razorpay) on this period's online transactions." />
            </span>
            <span className="font-semibold text-secondary-800">{money(payout_breakdown.gateway_fee_total)}</span>
          </div>
          <div>
            <span className="text-secondary-400 flex items-center gap-1 text-xs uppercase tracking-wide">
              Tax
              <InfoTip text="Tax (GST) charged on the gateway fee, deducted from online transactions." />
            </span>
            <span className="font-semibold text-secondary-800">{money(payout_breakdown.tax_total)}</span>
          </div>
          <div>
            <span className="text-red-500 flex items-center gap-1 text-xs uppercase tracking-wide">
              Refunded
              <InfoTip text="Total amount refunded to patients this period, across all transactions." />
            </span>
            <span className="font-semibold text-red-600">{money(payout_breakdown.refunded_total)}</span>
          </div>
          <div>
            <span className="text-amber-500 flex items-center gap-1 text-xs uppercase tracking-wide">
              Unpaid Invoices
              <InfoTip text="Number of appointments in this period, dated today or later, that are still Unpaid — still collectible." />
            </span>
            <span className="font-semibold text-amber-600">{payout_breakdown.unpaid_count}</span>
          </div>
          <button type="button" onClick={() => goToDetails("missed_revenue")} className="text-left hover:opacity-75 transition-opacity">
            <span className="text-red-500 flex items-center gap-1 text-xs uppercase tracking-wide">
              Missed Revenue
              <InfoTip text="Money that will never reach you: appointments whose date has passed with no payment ever made, or paid online with no consultation ever recorded (ops will never pay those out without one)." />
            </span>
            <span className="font-semibold text-red-600">
              {money(payout_breakdown.missed_revenue)} <span className="text-secondary-400 font-normal">({payout_breakdown.missed_count})</span>
            </span>
          </button>
        </div>
      </Card>

      {/* Revenue by mode + Payment methods */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="text-lg font-bold text-secondary-800 mb-4 flex items-center gap-1.5">
            Revenue by Payment Mode
            <InfoTip text="Of this period's confirmed revenue (same scope as Total Earnings), how much came through Online payments (Razorpay) vs paid directly At Clinic (cash)." />
          </h3>
          <div className="w-full h-3 rounded-full bg-secondary-100 overflow-hidden flex mb-3">
            <div className="h-full bg-primary-500" style={{ width: `${onlinePct}%` }} />
            <div className="h-full bg-secondary-400" style={{ width: `${100 - onlinePct}%` }} />
          </div>
          <div className="flex justify-between text-sm">
            <span className="flex items-center gap-2 text-secondary-700">
              <span className="w-2.5 h-2.5 rounded-full bg-primary-500 inline-block" /> Online
            </span>
            <span className="font-semibold text-secondary-900">{money(revenue_by_payment_mode.online)}</span>
          </div>
          <div className="flex justify-between text-sm mt-2">
            <span className="flex items-center gap-2 text-secondary-700">
              <span className="w-2.5 h-2.5 rounded-full bg-secondary-400 inline-block" /> At Clinic
            </span>
            <span className="font-semibold text-secondary-900">{money(revenue_by_payment_mode.at_clinic)}</span>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-lg font-bold text-secondary-800 mb-4 flex items-center gap-1.5">
            Payment Methods
            <InfoTip text="Online payments broken down by the exact method Razorpay recorded (UPI, Card, Netbanking, etc). 'Cash / At Clinic' covers all at-clinic payments; 'Unknown' means the method couldn't be read from the payment record (usually an older or test transaction)." />
          </h3>
          {payment_methods.length === 0 ? (
            <p className="text-secondary-400 italic text-sm">No paid transactions this period.</p>
          ) : (
            <div className="space-y-2.5">
              {payment_methods.map((m) => (
                <div key={m.method} className="flex justify-between items-center text-sm">
                  <span className="text-secondary-700">{METHOD_LABELS[m.method] || m.method}</span>
                  <span className="text-secondary-500">
                    {money(m.amount)} <span className="text-secondary-400">({m.count})</span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Recent transactions */}
      <div>
        <h3 className="text-lg font-bold text-secondary-800 mb-4 px-1 flex items-center gap-1.5">
          Recent Transactions
          <InfoTip text="The 10 most recent paid appointments in this period. Unpaid appointments aren't listed here — see Pending Collections above." />
        </h3>
        <Card className="overflow-hidden">
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-secondary-50/50 border-b border-secondary-100 text-xs uppercase tracking-wider text-secondary-500 font-semibold">
                  <th className="px-6 py-4">Date</th>
                  <th className="px-6 py-4">{showDoctorColumn ? "Doctor" : "Patient"}</th>
                  <th className="px-6 py-4">Method</th>
                  <th className="px-6 py-4 text-right">Amount</th>
                  <th className="px-6 py-4 text-right">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-secondary-100">
                {recent_transactions.length > 0 ? (
                  recent_transactions.map((t) => (
                    <tr key={t.appointment_id} className="hover:bg-secondary-50/30 transition-colors">
                      <td className="px-6 py-4 text-sm text-secondary-500">{t.date}</td>
                      <td className="px-6 py-4 font-medium text-secondary-900">
                        {(showDoctorColumn ? t.doctor_name : t.patient_name) || "Unknown"}
                      </td>
                      <td className="px-6 py-4 text-sm text-secondary-500">{METHOD_LABELS[t.method] || t.method}</td>
                      <td className="px-6 py-4 text-right font-semibold text-secondary-900">{money(t.amount)}</td>
                      <td className="px-6 py-4 text-right">
                        <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-green-100 text-green-700">
                          {t.status}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-secondary-400 italic">
                      No transactions this period.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="md:hidden divide-y divide-secondary-100">
            {recent_transactions.length > 0 ? (
              recent_transactions.map((t) => (
                <div key={t.appointment_id} className="p-4 hover:bg-secondary-50/30 transition-colors">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-bold text-secondary-900 text-base">
                        {(showDoctorColumn ? t.doctor_name : t.patient_name) || "Unknown"}
                      </div>
                      <div className="text-xs text-secondary-500 mt-1">{t.date} · {METHOD_LABELS[t.method] || t.method}</div>
                    </div>
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-green-100 text-green-700 whitespace-nowrap">
                      {t.status}
                    </span>
                  </div>
                  <div className="text-right font-semibold text-secondary-900 mt-2">{money(t.amount)}</div>
                </div>
              ))
            ) : (
              <div className="p-8 text-center text-secondary-400 italic">No transactions this period.</div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
